import axios, { AxiosError } from 'axios';

/**
 * API client.
 *
 * The single place the frontend talks to our backend. It never talks to SaturnShift or
 * PowerPool directly: the merchant token and provisioning credentials are server-side
 * only, and a browser must never be in a position to assert that a payment succeeded.
 *
 * It is also the only place that knows what an order costs -- by asking. No price, odds
 * figure, or address verdict is computed in the browser.
 */

export const api = axios.create({
  // Empty in development: vite.config.ts proxies /api to the backend, which keeps the
  // browser on one origin so session cookies behave.
  baseURL: import.meta.env.VITE_API_BASE_URL || '',
  withCredentials: true,
  timeout: 20_000,
});

export interface ApiErrorBody {
  error?: {
    code?: string;
    message?: string;
    details?: Record<string, unknown>;
  };
}

/** Pull a human-readable message out of whatever the backend or network returned. */
export function apiErrorMessage(error: unknown, fallback = 'Something went wrong.'): string {
  const axiosError = error as AxiosError<ApiErrorBody>;
  return axiosError?.response?.data?.error?.message ?? fallback;
}

export function apiErrorStatus(error: unknown): number | undefined {
  return (error as AxiosError)?.response?.status;
}

export function apiErrorCode(error: unknown): string | undefined {
  const axiosError = error as AxiosError<ApiErrorBody>;
  return axiosError?.response?.data?.error?.code;
}

/**
 * True when the server refused because no price exists for the requested configuration.
 *
 * That is a commercial gap, not a customer mistake, and the UI says so differently.
 */
export function isPricingUnavailable(error: unknown): boolean {
  return apiErrorStatus(error) === 422 && apiErrorCode(error) === 'pricing_unavailable';
}

// --- Storefront configuration -----------------------------------------------

export interface Tier {
  id: string;
  label: string;
  hashrateTh: number;
  displayHashrate: string;
  /** Authoritative price in cents, keyed by term length. Never computed here. */
  priceCentsByTermDays: Record<string, number>;
  /** Effective rates derived server-side from the authoritative price. */
  effectiveByTermDays: Record<string, { centsPerDay: number; centsPerThOverTerm: number }>;
  /**
   * How much cheaper this tier's per-TH rate is than the smallest tier's, in basis
   * points, per duration. Calculated server-side from the authoritative prices --
   * presentation only, and never used to price anything.
   */
  savingsBasisPointsByTermDays: Record<string, number>;
  /**
   * Savings per unit against the smallest band at the SHORTEST term, per duration --
   * so it moves with both hashrate and rental length. Server-derived; the browser only
   * rounds it for display.
   */
  combinedSavingsBasisPointsByTermDays: Record<string, number>;
}

export interface StorefrontConfig {
  currency: string;
  pricingVersion: string;
  tiers: Tier[];
  termDaysOptions: number[];
  miningModes: string[];
  customHashrate: {
    /** Whether an amount between tiers can be bought. Read, never assumed. */
    available: boolean;
    /** The range the server can actually price. The slider is bounded by these. */
    minTh: number;
    maxTh: number;
    incrementTh: number;
    /**
     * The pricing bands, for labelling only. Deliberately carries NO rate: the browser
     * is never given the means to compute a price.
     */
    brackets: Array<{
      id: string;
      label: string;
      anchorHashrateTh: number;
      displayHashrate: string;
      minTh: number;
      maxTh: number | null;
    }>;
    /**
     * PowerPool's confirmed allocation range. A separate question from pricing, and a
     * much wider one — the slider uses `minTh`/`maxTh`/`incrementTh` above, never these.
     *
     * The bounds differ in inclusivity, so they are reported separately: 0 is not
     * allocatable, 1e9 is, and exactly 1 TH/s is valid. `incrementTh` is null because
     * PowerPool constrains no increment — that is the answer, not a gap. Our own
     * 10 TH/s step is a commercial rule.
     */
    allocationLimits: {
      minTh: number;
      maxTh: number;
      minExclusive: boolean;
      maxInclusive: boolean;
      incrementTh: number | null;
      placeholder: boolean;
    };
  };
  payout: {
    network: string;
    supportedAddressKinds: string[];
    powerpoolIdSupported: boolean;
  };
}

export async function fetchStorefrontConfig(signal?: AbortSignal): Promise<StorefrontConfig> {
  const { data } = await api.get<StorefrontConfig>('/api/storefront/config', { signal });
  return data;
}

// --- Quote -------------------------------------------------------------------

/**
 * The authoritative price for one configuration.
 *
 * Every figure here is the server's. Nothing in this file multiplies a rate, applies a
 * bracket, or derives a saving -- a price the browser calculated is a price the server
 * never agreed to.
 */
export interface Quote {
  hashrateTh: number;
  termDays: number;
  currency: string;
  pricingVersion: string;
  totalCents: number;
  subtotalCents: number;
  discountAmountCents: number;
  effective: { centsPerDay: number; centsPerThOverTerm: number };
  /** Saving per TH against the smallest band at the SAME duration. Presentation only. */
  savingsBasisPoints: number;
  /**
   * Saving across BOTH dimensions -- hashrate and duration -- against the smallest band
   * at the shortest term. This is what the storefront displays. Presentation only.
   */
  combinedSavingsBasisPoints: number;
  /**
   * The same combined figure for every duration at this hashrate, keyed by term.
   *
   * Comes from the quote rather than the tier catalogue because a custom amount has no
   * tier: only the server knows which band prices 610 TH/s, so only the server can say
   * what each duration saves at that amount.
   */
  combinedSavingsBasisPointsByTermDays: Record<string, number>;
  /**
   * TOTAL SAVED, CALCULATED SERVER-SIDE.
   *
   * `totalCents` already contains every discount — band, duration and any code — so the
   * saving is one subtraction the server performs against the reference price. The
   * browser renders these; it never composes percentages or reconstructs a denominator,
   * because a figure derived that way would disagree with the total by a cent or two and
   * read as a bug.
   */
  referenceSubtotalCents: number;
  totalSavedCents: number;
  totalSavedBasisPoints: number;
  isCustomHashrate: boolean;
  tier: { id: string; label: string; hashrateTh: number; displayHashrate: string } | null;
  bracket: {
    id: string;
    label: string;
    anchorHashrateTh: number;
    displayHashrate: string;
    minTh: number;
    maxTh: number | null;
  };
  /**
   * The discount code's verdict, when one was submitted. `null` when none was.
   *
   * A rejected code is NOT an error: the quote comes back undiscounted and authoritative
   * with the server's reason attached, so the page keeps a real total.
   */
  discount:
    | { applied: true; code: string; amountCents: number }
    | { applied: false; reason: string }
    | null;
}

/**
 * Ask what a configuration costs.
 *
 * Grants nothing: no reservation, no price lock. Order creation re-quotes server-side
 * and that quote is the one that binds.
 */
export async function fetchQuote(
  params: { hashrateTh: number; termDays: number; discountCode?: string | null },
  signal?: AbortSignal,
): Promise<Quote> {
  const { data } = await api.get<{ quote: Quote }>('/api/storefront/quote', {
    params: {
      hashrateTh: params.hashrateTh,
      termDays: params.termDays,
      // Omitted entirely when absent, so the request is unchanged for the ordinary case.
      ...(params.discountCode ? { discountCode: params.discountCode } : {}),
    },
    signal,
  });
  return data.quote;
}

// --- Odds --------------------------------------------------------------------

export type DifficultyStatus = 'fresh' | 'refreshed' | 'stale';

export interface Odds {
  hashrateTh: number;
  termDays: number;
  difficulty: number;
  expectedBlocks: number;
  probability: number;
  probabilityPercent: number;
  /** Null when the probability underflows to exactly zero. */
  oneInChance: number | null;
  expectedSecondsPerBlock: number;
  difficultySource: {
    source: string;
    status: DifficultyStatus;
    retrievedAt: string;
  };
}

export async function fetchOdds(
  params: { hashrateTh: number; termDays: number },
  signal?: AbortSignal,
): Promise<Odds> {
  const { data } = await api.get<{ odds: Odds }>('/api/storefront/odds', {
    params: { hashrateTh: params.hashrateTh, termDays: params.termDays },
    signal,
  });
  return data.odds;
}

/**
 * What one block is worth right now.
 *
 * `available: false` is a NORMAL answer, not an error — the server returns 200 when the
 * BTC price cannot be fetched, because this figure is supplementary and a price outage
 * must not make the page look broken. The browser renders nothing in that case, and
 * never a remembered or estimated price.
 *
 * The value is computed server-side, like every other money figure. The browser is never
 * given a BTC price to multiply by a subsidy of its own.
 */
export type WinningValue =
  | { available: false }
  | {
      available: true;
      currency: string;
      /** Integer minor units, as always. */
      winningValueCents: number;
      blockSubsidyBtc: number;
      priceSource: { source: string; status: string; retrievedAt: string };
    };

export async function fetchWinningValue(signal?: AbortSignal): Promise<WinningValue> {
  const { data } = await api.get<WinningValue>('/api/storefront/winning-value', { signal });
  return data;
}

// --- Payout address ----------------------------------------------------------

export interface PayoutValidation {
  valid: boolean;
  kind?: string;
  network?: string;
  reason?: string;
  code?: string;
  powerpoolIdPending?: boolean;
}

/**
 * Ask the server whether an address is usable.
 *
 * Advisory: it exists so the field can give live feedback without the browser carrying
 * its own Bitcoin address implementation, which would inevitably drift from the
 * authoritative one. Order creation revalidates regardless.
 */
export async function validatePayoutAddress(
  address: string,
  signal?: AbortSignal,
): Promise<PayoutValidation> {
  const { data } = await api.post<PayoutValidation>(
    '/api/storefront/validate-payout-address',
    { address },
    { signal },
  );
  return data;
}

// --- Orders ------------------------------------------------------------------

/**
 * Exactly the fields the server accepts.
 *
 * No price, total, tier id, or state. The server rejects those by name, and the type
 * makes it hard to send one by accident.
 */
export interface CreateOrderRequest {
  hashrateTh: number;
  termDays: number;
  miningMode: string;
  payoutAddress: string;
  email: string;
  /**
   * The CODE only. Never an amount, a percentage or a total -- the server rejects those
   * by name, and it resolves and revalidates this from scratch at order creation.
   */
  discountCode?: string;
}

export type PaymentState = 'pending_payment' | 'paid' | 'failed' | 'expired' | 'refunded';
export type ProvisioningState =
  | 'not_provisioned'
  | 'provisioning'
  | 'active'
  | 'expired'
  | 'provision_failed';

/**
 * The odds as they stood WHEN THE ORDER WAS PLACED, read from the frozen snapshot.
 *
 * Never recomputed on read: difficulty moves, and showing a different figure than the
 * customer agreed to would be the one thing a purchase record exists to prevent.
 * `available: false` is a normal answer — a difficulty outage never blocked a sale.
 */
export type OrderOdds =
  | {
      available: true;
      difficulty: number;
      expectedBlocks: number;
      probability: number;
      probabilityPercent: number;
      oneInChance: number | null;
      expectedSecondsPerBlock: number;
      difficultySource: { source: string | null; status: string | null; retrievedAt: string | null };
    }
  | { available: false; reason: string | null }
  | null;

export interface Order {
  orderNumber: string;
  status: string;
  /** Both authoritative state machines, as the server reports them. */
  states: { payment: PaymentState; provisioning: ProvisioningState };
  configuration: {
    hashrateTh: number;
    termDays: number;
    miningMode: string;
    tier: { id: string; label: string; displayHashrate: string } | null;
    isCustomHashrate: boolean;
  };
  payout: { address: string; kind: string | null };
  pricing: {
    currency: string;
    subtotalCents: number;
    discountAmountCents: number;
    totalCents: number;
    discountCode: string | null;
    effective: { centsPerDay: number; centsPerThOverTerm: number } | null;
    pricingVersion: string | null;
    /**
     * What this purchase saved, FROZEN at creation — never recomputed from today's
     * catalogue. Null for orders placed before it was captured, and the UI then omits the
     * savings line rather than inventing one.
     *
     * `totalSavedCents` is already net of any discount code, so a promo shown beside it
     * explains that figure and must not be added to it again.
     */
    savings: {
      referenceSubtotalCents: number;
      totalSavedCents: number;
      totalSavedBasisPoints: number;
    } | null;
  };
  oddsAtPurchase: OrderOdds;
  createdAt: string;
}

/**
 * The public SaturnShift configuration for ONE order, composed server-side.
 *
 * The browser assembles none of it — in particular `redirectUrl`, which the server
 * builds from `PUBLIC_WEB_URL` and the persisted order number. A redirect target taken
 * from the client would be an open redirect on the page a customer lands on after
 * paying.
 *
 * NOTHING HERE IS SECRET. The public key is browser-safe by design; the OAuth
 * credentials, the webhook signing secret and the merchant id are server-only and are
 * not part of this shape.
 *
 * `publicKey`/`apiOrigin` are null when the server has no SaturnShift configuration —
 * checkout then reports itself unavailable rather than requesting `undefined/checkout.js`.
 *
 * THE SAME SHAPE ARRIVES BY TWO ROUTES, deliberately: on the create-order response, and
 * on the authenticated single-order response for an order that has not been paid yet.
 * One shape, one `startCheckout`, one set of rules — a customer resuming payment days
 * later takes exactly the path they would have taken immediately.
 */
export interface CheckoutConfig {
  publicKey: string | null;
  apiOrigin: string | null;
  redirectUrl: string;
  /**
   * The customer's email, when the server knows one, for SaturnShift to PREFILL the
   * receipt-email field with. Editable there, and neither identity nor authorization.
   *
   * OPTIONAL BECAUSE IT IS GENUINELY ABSENT SOMETIMES, not as a convenience: the server
   * omits the key entirely rather than sending null. Checkout must never depend on it.
   */
  customerEmail?: string;
}

export interface CreatedOrder {
  order: Order;
  /** Whether the confirmation email actually went out. Best effort by design. */
  receipt: { sent: boolean };
  checkout: CheckoutConfig;
}

export async function createOrder(request: CreateOrderRequest): Promise<CreatedOrder> {
  const { data } = await api.post<CreatedOrder>('/api/orders', request);
  return data;
}

// --- Payment status (the return page) -----------------------------------------

/**
 * What the server says about a payment.
 *
 * A CLOSED VOCABULARY, MAPPED SERVER-SIDE. SaturnShift's ten statuses and three booleans
 * never reach the browser; the backend collapses them to these, so nothing here has to
 * interpret a provider status — or could get that interpretation wrong.
 */
export type PaymentStatus =
  /** Confirmed and recorded. The only value that may be shown as success. */
  | 'paid'
  /** SaturnShift has the payment in flight. */
  | 'processing'
  /** We could not get a usable answer. Neutral: the webhook may still complete it. */
  | 'confirming_payment'
  /** SaturnShift has no payment for this order yet. */
  | 'awaiting_payment'
  | 'failed'
  | 'expired'
  | 'refunded'
  /** A refund, dispute or mismatch. Never shown as success. */
  | 'needs_review';

/**
 * The public payment-status shape.
 *
 * Deliberately tiny, because the endpoint is reachable by anyone holding an order number.
 * There is no email, payout address, snapshot, discount, or provider identifier in it —
 * see backend/src/services/paymentStatusService.js for the full exclusion list.
 */
/**
 * What the customer gets, as opposed to what they paid.
 *
 * Payment and provisioning are separate machines, and this page must never conflate them:
 * an order can be paid while the hashrate is still being allocated. Five internal
 * provisioning states collapse to these four, so no operational state name — least of all
 * `provision_failed` — ever reaches a customer.
 */
export type Fulfilment = 'active' | 'preparing' | 'ended' | 'not_started';

export interface PaymentStatusResponse {
  orderNumber: string;
  status: PaymentStatus;
  paymentState: PaymentState;
  currency: string;
  totalCents: number;
  paidAt: string | null;

  /** Whether the rental is running. NEVER inferred from `status`. */
  fulfilment: Fulfilment;

  configuration: { hashrateTh: number; termDays: number; miningMode: string };
  /** Masked server-side. The full address is never published on this route. */
  payoutAddress: string;
  /** PowerPool's own term, once it has provisioned. Null until then. */
  startsAt: string | null;
  endsAt: string | null;

  /**
   * The provider's live view, or null.
   *
   * Null for anyone who is not the signed-in owner, and null in mock mode for everyone —
   * a Solo watch URL embeds the full payout address this DTO masks. The server decides;
   * the browser never constructs, rebuilds or inspects this value.
   */
  watchUrl: string | null;
}

/**
 * Ask the server about an order's payment.
 *
 * ONE CALL, ONE ANSWER. If the order is still pending, the BACKEND performs a single
 * server-side reconciliation against SaturnShift — the browser never talks to SaturnShift
 * and holds no token that would let it. Calling this again is a second check, not a second
 * order and not a second checkout.
 */
export async function fetchPaymentStatus(
  orderNumber: string,
  signal?: AbortSignal,
): Promise<PaymentStatusResponse> {
  const { data } = await api.get<PaymentStatusResponse>(
    `/api/orders/${encodeURIComponent(orderNumber)}/payment-status`,
    { signal },
  );
  return data;
}

// --- Authentication ----------------------------------------------------------

export interface Customer {
  email: string;
}

/**
 * Ask for a sign-in link.
 *
 * The server answers identically whether or not the address has an account, so this
 * resolving successfully says nothing about whether an email was sent. The UI must not
 * pretend otherwise.
 */
export async function requestMagicLink(email: string): Promise<{ message: string }> {
  const { data } = await api.post<{ message: string }>('/api/auth/magic-link', { email });
  return data;
}

/**
 * Exchange a sign-in link for a session.
 *
 * The session arrives as an httpOnly cookie the browser stores automatically; there is
 * no token in the response for JavaScript to hold, and that is deliberate.
 */
export async function verifyMagicLink(token: string): Promise<Customer> {
  const { data } = await api.post<{ customer: Customer }>('/api/auth/verify', { token });
  return data.customer;
}

/** The signed-in customer, or null. */
export async function fetchCurrentCustomer(signal?: AbortSignal): Promise<Customer | null> {
  const { data } = await api.get<{ customer: Customer | null }>('/api/auth/me', { signal });
  return data.customer;
}

/** Sign out. Revokes the session server-side, not just in the browser. */
export async function logout(): Promise<void> {
  await api.post('/api/auth/logout');
}

// --- The signed-in customer's orders ------------------------------------------

export async function fetchMyOrders(signal?: AbortSignal): Promise<Order[]> {
  const { data } = await api.get<{ orders: Order[] }>('/api/account/orders', { signal });
  return data.orders;
}

/**
 * One of the signed-in customer's own orders, with the two owner-only extras.
 *
 * `checkout` IS THE SERVER'S ANSWER TO "CAN THIS STILL BE PAID?" — non-null only while
 * `paid` remains reachable from the order's payment state. The page renders the resume
 * action when a block is present and does not carry a second copy of that rule; a paid,
 * failed, expired or refunded order simply arrives without one.
 *
 * `watchUrl` IS POWERPOOL'S OWN LIVE VIEW, verbatim, and null unless the allocation is
 * actually running in live mode. The browser never constructs it, never rebuilds it from
 * the payout address or the team, and never inspects it — a Solo URL embeds the full
 * wallet this account view otherwise masks, which is why the server decides.
 *
 * `payment` IS WHAT THE PROCESSOR SAID, JUST NOW, and null when nothing was asked —
 * which is every order that is not awaiting payment. The order state alone cannot
 * distinguish "you still owe us" from "we could not reach the processor", and only one
 * of those is worth a Retry button.
 */
export type PaymentConfirmation =
  /** Settled. The order state will say so too. */
  | 'paid'
  /** In flight at the processor. A second checkout would race it. */
  | 'processing'
  /** We could not get an answer. Temporary, and retryable — NOT evidence of non-payment. */
  | 'confirming_payment'
  /** The processor has no payment for this order. It is genuinely unpaid. */
  | 'awaiting_payment'
  /** Terminal, or a person has to look at it. */
  | 'failed'
  | 'expired'
  | 'refunded'
  | 'needs_review';

export interface MyOrderView {
  order: Order;
  checkout: CheckoutConfig | null;
  watchUrl: string | null;
  payment: { status: PaymentConfirmation } | null;
}

export async function fetchMyOrder(
  orderNumber: string,
  signal?: AbortSignal,
): Promise<MyOrderView> {
  const { data } = await api.get<MyOrderView>(
    `/api/account/orders/${encodeURIComponent(orderNumber)}`,
    { signal },
  );
  return {
    order: data.order,
    checkout: data.checkout ?? null,
    watchUrl: data.watchUrl ?? null,
    payment: data.payment ?? null,
  };
}

/**
 * The result of moving ONE order's payout address.
 *
 * EVERY FIELD IS THE SERVER'S ANSWER, not an echo of what was asked. `payoutAddress`
 * comes back MASKED, and it reflects what the mining provider actually confirmed, so a
 * provider that quietly kept the old wallet cannot be rendered as a success. The page
 * therefore replaces what it displays from THIS value and never from the input field.
 *
 * `changed` is false when the address was already the current one. That is a real
 * outcome, not a failure.
 *
 * `watchUrl` is the live view, which moves with the wallet for a solo rental. Null when
 * there is nothing customer-presentable; the browser never constructs it.
 */
/**
 * What the server returns when a payout change has been REQUESTED.
 *
 * Note what is absent: no `changed`, no `watchUrl`. Nothing has moved, and a shape that
 * could be read as "done" would be a lie the UI would faithfully repeat.
 */
export interface PayoutAddressChangeRequested {
  orderNumber: string;
  status: 'confirmation_required';
  /** Masked, as everywhere else. Shown back so the customer knows which request this is. */
  payoutAddress: string;
  /** False when the mail provider was unreachable. The request still stands. */
  emailSent: boolean;
  expiresAt: string;
}

/**
 * What the server returns once a confirmation link has been claimed.
 *
 * ===========================================================================
 * TWO SHAPES, DISCRIMINATED BY `scope`, BECAUSE A LINK CAN CONFIRM TWO THINGS.
 *
 * A link issued from an order page moves ONE rental. A link issued from the account page
 * saves the default wallet and fans it out across every active rental. The server has
 * always answered with two different bodies; this type previously claimed there was only
 * one, so the account-default fields arrived as `undefined` and rendered as blanks.
 *
 * Narrow on `scope` before reading any field that is not on both.
 */
export interface PayoutOrderConfirmed {
  scope: 'order';
  orderNumber: string;
  changed: boolean;
  /** Masked, as everywhere else on this API. */
  payoutAddress: string;
  watchUrl: string | null;
}

/**
 * The account-default confirmation.
 *
 * NOTE THE FIELD NAME: `defaultPayoutAddress`, not `payoutAddress` -- and there is no
 * `orderNumber`, because a default change is not about one order. It carries the same
 * fan-out counts the unconfirmed save returns, so the page can say which rentals moved.
 */
export interface PayoutDefaultConfirmed extends PayoutDefaultUpdate {
  scope: 'account_default';
  changed: boolean;
}

export type PayoutChangeConfirmed = PayoutOrderConfirmed | PayoutDefaultConfirmed;

/** Narrows a claimed confirmation to the single-order case. */
export function isOrderConfirmation(
  result: PayoutChangeConfirmed,
): result is PayoutOrderConfirmed {
  return result.scope === 'order';
}

/**
 * REQUEST a change to where ONE order pays. It does not perform one.
 *
 * ===========================================================================
 * THIS USED TO APPLY THE CHANGE IMMEDIATELY.
 *
 * Phase 1 scope calls for an EMAIL-CONFIRMED change: a session alone must not be able to
 * redirect where a block reward is paid, because mined BTC goes from the Bitcoin network
 * straight to whatever address the pool holds and never passes through Metered Mining.
 *
 * The server now records the request and emails a single-use link. The address moves
 * when that link is claimed, not when this resolves. The return type says so.
 *
 * THE ONLY THING THAT TRAVELS IS AN ADDRESS. The allocation identity, hashrate, term,
 * mode and team are reconstructed server-side from the frozen order; the API refuses
 * them by name if a caller tries to send them.
 *
 * This does NOT touch the account default wallet.
 */
export async function requestOrderPayoutAddressChange(
  orderNumber: string,
  payoutAddress: string,
): Promise<PayoutAddressChangeRequested> {
  const { data } = await api.patch<PayoutAddressChangeRequested>(
    `/api/account/orders/${encodeURIComponent(orderNumber)}/payout-address`,
    { payoutAddress },
  );
  return data;
}

/**
 * Claim a confirmation link and apply the change.
 *
 * NO SESSION IS REQUIRED OR ISSUED. The token is the authorization -- single-use,
 * time-limited, and bound server-side to one customer, one order and one address -- so a
 * customer can confirm from the device their email is on. It is deliberately not a
 * sign-in: confirming a payout change must never log anyone in.
 */
export async function confirmPayoutAddressChange(
  token: string,
): Promise<PayoutChangeConfirmed> {
  const { data } = await api.post<PayoutChangeConfirmed>(
    '/api/account/payout-address/confirm',
    { token },
  );
  return data;
}

/**
 * The customer's saved DEFAULT payout wallet, or null.
 *
 * Returned IN FULL, unlike every order-scoped representation, because this is a setting
 * the customer reads back and edits. It is their own wallet on a session-scoped route,
 * and deliberately not part of the session bootstrap.
 *
 * NULL MEANS NULL. Never inferred from an order: an address used once for one rental is
 * not a statement about where future ones should pay.
 */
export interface PayoutDefault {
  payoutAddress: string | null;
  kind: string | null;
}

export async function fetchPayoutDefault(signal?: AbortSignal): Promise<PayoutDefault> {
  const { data } = await api.get<PayoutDefault>('/api/account/payout-address', { signal });
  return data;
}

/**
 * What happened when the default wallet changed.
 *
 * THE DEFAULT IS SAVED FIRST AND SEPARATELY. The counts describe a second thing: how
 * many live rentals were moved onto it. A rental that could not be moved does NOT mean
 * the wallet failed to save, and the UI must not say it did.
 *
 * Counts come from the server. The browser never derives them, because it does not know
 * which orders were eligible.
 */
export interface PayoutDefaultUpdate {
  defaultPayoutAddress: string;
  applied: boolean;
  eligibleCount: number;
  updatedCount: number;
  failedCount: number;
  orders: Array<{ orderNumber: string; outcome: string }>;
}

/**
 * What the server returns when the FAN-OUT was asked for and needs confirming.
 *
 * Note what is absent: no counts, no `orders`. Nothing has been saved and nothing has
 * moved, so there is nothing to count -- and a shape that could be read as "done" would
 * be a lie the UI would faithfully repeat.
 */
export interface PayoutDefaultChangeRequested {
  status: 'confirmation_required';
  defaultPayoutAddress: string;
  applied: false;
  /** False when the mail provider was unreachable. The request still stands. */
  emailSent: boolean;
  expiresAt: string;
}

/**
 * Save the default wallet, optionally moving live rentals onto it.
 *
 * ===========================================================================
 * TWO OUTCOMES, AND THE FLAG DECIDES WHICH.
 *
 * Saving the wallet ALONE is a preference about future orders: it redirects nothing that
 * is currently mining, so it applies immediately and returns `PayoutDefaultUpdate`.
 *
 * Asking to move live rentals is the same money-redirecting act the order page performs,
 * several times over -- so it takes the same email confirmation and returns
 * `PayoutDefaultChangeRequested` instead. Discriminate on `status` before reading any
 * count; the requested shape has none.
 *
 * Sends the address and the flag. Nothing else: the allocation identity, hashrate, term,
 * mode and team are reconstructed server-side, and the API refuses them by name.
 */
export async function updateDefaultPayoutAddress(
  payoutAddress: string,
  applyToActiveOrders: boolean,
): Promise<PayoutDefaultUpdate | PayoutDefaultChangeRequested> {
  const { data } = await api.patch<PayoutDefaultUpdate | PayoutDefaultChangeRequested>(
    '/api/account/payout-address',
    { payoutAddress, applyToActiveOrders },
  );
  return data;
}

/** Narrows the two outcomes of `updateDefaultPayoutAddress`. */
export function isPayoutChangeRequested(
  result: PayoutDefaultUpdate | PayoutDefaultChangeRequested,
): result is PayoutDefaultChangeRequested {
  return (result as PayoutDefaultChangeRequested).status === 'confirmation_required';
}

// --- Support ------------------------------------------------------------------

/**
 * The categories the support form offers.
 *
 * The KEY is what travels; the label is only ever displayed. The server holds the same
 * allowlist and looks the label up itself, so the email subject line is never composed
 * from anything the browser sent -- see backend/src/services/supportService.js.
 */
export const SUPPORT_SUBJECTS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'order', label: 'Order question' },
  { value: 'payment', label: 'Payment question' },
  { value: 'mining', label: 'Mining / hashrate question' },
  { value: 'payout_address', label: 'Payout address question' },
  { value: 'account_access', label: 'Account access' },
  { value: 'other', label: 'Other' },
];

/** Mirrors MESSAGE_MAX_LENGTH in the support service. The server is the authority. */
export const SUPPORT_MESSAGE_MAX_LENGTH = 5_000;

export interface SupportRequest {
  email: string;
  subject: string;
  message: string;
}

/**
 * Send a message to support.
 *
 * The browser never talks to a mail provider. This posts to Metered Mining, which
 * validates, rate-limits, and sends through the configured mailer.
 */
export async function sendSupportMessage(request: SupportRequest): Promise<void> {
  await api.post('/api/support', request);
}
